import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CashBookTab = ({ getAuthHeader, fromDate, toDate, accounts }) => {
  const [cashRegister, setCashRegister] = useState({ entries: [], summary: {}, pagination: null });
  const [showCashEntry, setShowCashEntry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  
  const today = new Date().toISOString().split('T')[0];
  const [entryForm, setEntryForm] = useState({
    entry_date: today,
    transaction_type: 'receipt',
    contra_account_id: '',
    amount: '',
    narration: '',
    reference_no: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const fetchCashRegister = async (page = 1) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/accounting/cash/register?from_date=${fromDate}&to_date=${toDate}&page=${page}&page_size=${pageSize}`,
        { headers: getAuthHeader() }
      );
      setCashRegister(res.data);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch cash register');
    }
  };

  useEffect(() => {
    fetchCashRegister(1);
  }, [fromDate, toDate]);

  const handleCashEntry = async () => {
    const cashAccount = accounts.find(a => a.account_group === 'cash_in_hand');
    if (!cashAccount) {
      toast.error('Cash account not found');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/accounting/cash/entry`, {
        ...entryForm,
        account_id: cashAccount.id,
        amount: parseFloat(entryForm.amount)
      }, { headers: getAuthHeader() });
      
      toast.success('Cash entry created');
      setShowCashEntry(false);
      fetchCashRegister(1);
      setEntryForm({
        entry_date: today,
        transaction_type: 'receipt',
        contra_account_id: '',
        amount: '',
        narration: '',
        reference_no: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    }
  };

  const pagination = cashRegister.pagination;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Cash Book</CardTitle>
            <CardDescription>Cash receipts and payments</CardDescription>
          </div>
          <Button onClick={() => setShowCashEntry(true)} data-testid="new-cash-entry-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </CardHeader>
        <CardContent>
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-muted rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Opening Balance</p>
              <p className="text-lg font-bold">{formatCurrency(cashRegister.summary?.opening_balance)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Receipts</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(cashRegister.summary?.total_receipts)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Payments</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(cashRegister.summary?.total_payments)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Closing Balance</p>
              <p className="text-lg font-bold">{formatCurrency(cashRegister.summary?.closing_balance)}</p>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Voucher No</TableHead>
                <TableHead>Narration</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cashRegister.entries?.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.entry_date}</TableCell>
                  <TableCell className="font-mono">{entry.voucher_no}</TableCell>
                  <TableCell>{entry.narration}</TableCell>
                  <TableCell>
                    <Badge variant={entry.transaction_type === 'receipt' ? 'default' : 'destructive'}>
                      {entry.transaction_type === 'receipt' ? (
                        <><ArrowDownRight className="h-3 w-3 mr-1" /> Receipt</>
                      ) : (
                        <><ArrowUpRight className="h-3 w-3 mr-1" /> Payment</>
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className={`text-right font-medium ${
                    entry.transaction_type === 'receipt' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(entry.amount)}
                  </TableCell>
                </TableRow>
              ))}
              {cashRegister.entries?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No cash entries found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} entries
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchCashRegister(currentPage - 1)}
                  disabled={!pagination.has_prev}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm px-2">
                  Page {currentPage} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchCashRegister(currentPage + 1)}
                  disabled={!pagination.has_next}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cash Entry Dialog */}
      <Dialog open={showCashEntry} onOpenChange={setShowCashEntry}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Cash Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={entryForm.entry_date}
                  onChange={(e) => setEntryForm({ ...entryForm, entry_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={entryForm.transaction_type}
                  onValueChange={(v) => setEntryForm({ ...entryForm, transaction_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receipt">Receipt</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Account (Dr/Cr)</Label>
              <Select
                value={entryForm.contra_account_id}
                onValueChange={(v) => setEntryForm({ ...entryForm, contra_account_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter(a => a.account_group !== 'cash_in_hand').map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.account_code} - {acc.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                value={entryForm.amount}
                onChange={(e) => setEntryForm({ ...entryForm, amount: e.target.value })}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <Label>Narration</Label>
              <Input
                value={entryForm.narration}
                onChange={(e) => setEntryForm({ ...entryForm, narration: e.target.value })}
                placeholder="Description of transaction"
              />
            </div>
            <div>
              <Label>Reference No (Optional)</Label>
              <Input
                value={entryForm.reference_no}
                onChange={(e) => setEntryForm({ ...entryForm, reference_no: e.target.value })}
                placeholder="Receipt/Invoice number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCashEntry(false)}>Cancel</Button>
            <Button onClick={handleCashEntry} data-testid="save-cash-entry-btn">Save Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CashBookTab;
